import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Phone, MessageSquare, Users, Utensils, Music, Sparkles, ExternalLink } from "lucide-react";
import Availability from "@/components/site/Availability";
import { AIRBNB_URL, IMAGES } from "@/data/property";

const HOST_PHONE_DISPLAY = "(210) 274-3556";
const HOST_PHONE_TEL = "+12102743556";
const HOST_PHONE_SMS = "12102743556";

const PACKAGES = [
  {
    name: "Intimate Ceremony",
    guests: "Up to 40",
    highlight: "Perfect for elopements, vow renewals, and rehearsal dinners under Hill Country stars.",
    features: ["2-night minimum", "Sunset ceremony on the upper deck", "Caterers and vendor list available", "Dedicated coordinator available"],
  },
  {
    name: "Signature Wedding",
    guests: "40 – 80",
    highlight: "The full estate experience: ceremony deck, dinner reception on the main level, dancing beside the pool.",
    features: ["3-night minimum", "Bridal & groom suites included", "Space for a live band or DJ", "Caterers and vendor list available", "Dedicated coordinator available"],
    featured: true,
  },
  {
    name: "Celebration Weekend",
    guests: "80 – 120",
    highlight: "Milestone birthdays, corporate retreats, and celebration weekends with vendor tents on the lower lawn.",
    features: ["2-night minimum", "Caterers and vendor list available", "Dedicated coordinator available", "Guest overflow parking coordination"],
  },
];

const LAYOUT = [
  { icon: Music, title: "Sunset Ceremony Deck", body: "Upper deck with panoramic sunset views over Canyon Lake — the moment your guests won't stop talking about." },
  { icon: Utensils, title: "Reception & Dining", body: "Great room with vaulted wood ceilings and floor-to-ceiling lake windows. Chef's kitchen with dinnerware for 24 built in, plus room for catering staging." },
  { icon: Sparkles, title: "Pool & Fire Pit After-Party", body: "Poolside cocktails, fire pit s'mores, and 8 private suites so every guest sleeps under the same roof." },
];

export default function Weddings() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF8]">
      {/* Hero */}
      <section className="relative min-h-[75vh] overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Grandview Estate at sunset — event-ready pool deck" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/40 via-[#0A192F]/25 to-[#0A192F]/70" />
        </div>
        <div className="relative z-10 h-full min-h-[75vh] flex flex-col justify-center max-w-6xl mx-auto px-6 pt-32 pb-20">
          <Link to="/" data-testid="weddings-back-link" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-body mb-8"><ArrowLeft size={14} /> Back to home</Link>
          <p className="text-xs uppercase tracking-[0.3em] text-[#1FB6C9] font-body">Weddings · Milestones · Corporate</p>
          <motion.h1
            initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="mt-4 text-white font-display text-5xl md:text-7xl leading-[0.95] max-w-3xl"
          >
            Say <em className="italic" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>"I do"</em> above the lake.
          </motion.h1>
          <p className="mt-6 max-w-2xl text-white/85 font-body text-lg">
            Grandview Estate is built for celebrations. Panoramic sunset ceremonies, indoor-outdoor receptions,
            and 8 private suites so every guest sleeps steps from the party.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <a href={`tel:${HOST_PHONE_TEL}`} data-testid="weddings-hero-call" className="inline-flex items-center gap-2 rounded-full bg-[#1FB6C9] px-8 py-4 font-display font-medium text-white shadow-xl shadow-[#1FB6C9]/30 hover:-translate-y-0.5 transition-transform">
              <Phone size={18} /> Call {HOST_PHONE_DISPLAY}
            </a>
            <a href={AIRBNB_URL} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/60 px-8 py-4 font-display font-medium text-white hover:bg-white hover:text-[#0E2A47] transition-colors">
              Check dates <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Capacity strip */}
      <section className="bg-[#0E2A47] text-white">
        <div className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
          {[
            { k: "Overnight", v: "24" },
            { k: "Ceremony", v: "Up to 120" },
            { k: "Bedrooms", v: "8 suites" },
            { k: "Grounds", v: "Lakefront" },
          ].map((s) => (
            <div key={s.k}>
              <div className="font-display text-4xl md:text-5xl text-[#1FB6C9]">{s.v}</div>
              <div className="mt-2 text-xs uppercase tracking-[0.25em] text-white/60 font-body">{s.k}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Layout */}
      <section className="py-24 max-w-6xl mx-auto px-6">
        <p className="text-xs uppercase tracking-[0.25em] text-[#1FB6C9] font-body">The Estate, Reimagined for Events</p>
        <h2 className="mt-4 font-display text-4xl md:text-5xl text-[#0E2A47] max-w-2xl">One venue. Three distinct moments.</h2>
        <div className="mt-14 grid md:grid-cols-3 gap-6">
          {LAYOUT.map((l) => (
            <div key={l.title} data-testid={`wedding-layout-${l.title.split(" ")[0].toLowerCase()}`} className="rounded-2xl bg-white border border-[#0E2A47]/8 p-8 hover:border-[#1FB6C9]/40 transition-colors">
              <l.icon size={22} className="text-[#1FB6C9]" />
              <div className="mt-6 font-display text-xl text-[#0E2A47]">{l.title}</div>
              <p className="mt-3 text-[#0E2A47]/70 font-body leading-relaxed">{l.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Packages */}
      <section className="bg-[#F1E8DA]/50 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.25em] text-[#1FB6C9] font-body">Event Packages</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl text-[#0E2A47] max-w-2xl">Three ways to celebrate at Grandview.</h2>
          <p className="mt-4 text-[#0E2A47]/70 font-body max-w-2xl">Every event is customized. These packages set the frame — final details, catering, and vendor coordination are quoted based on your date and guest count.</p>

          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {PACKAGES.map((p) => (
              <div key={p.name} data-testid={`wedding-package-${p.name.split(" ")[0].toLowerCase()}`} className={`rounded-2xl p-8 border ${p.featured ? "bg-[#0E2A47] text-white border-[#0E2A47]" : "bg-white border-[#0E2A47]/10"} relative`}>
                {p.featured && <div className="absolute -top-3 left-8 bg-[#d9a441] text-[#0E2A47] text-xs font-body font-medium uppercase tracking-widest px-3 py-1 rounded-full">Most popular</div>}
                <div className="font-display text-2xl">{p.name}</div>
                <div className={`mt-1 inline-flex items-center gap-1.5 text-sm font-body ${p.featured ? "text-[#1FB6C9]" : "text-[#0E2A47]/60"}`}><Users size={14} /> {p.guests} guests</div>
                <p className={`mt-4 font-body leading-relaxed ${p.featured ? "text-white/80" : "text-[#0E2A47]/70"}`}>{p.highlight}</p>
                <ul className={`mt-6 space-y-2 text-sm font-body ${p.featured ? "text-white/85" : "text-[#0E2A47]/80"}`}>
                  {p.features.map((f) => <li key={f} className="flex gap-2"><span className="text-[#1FB6C9]">·</span>{f}</li>)}
                </ul>
                <a
                  href={`tel:${HOST_PHONE_TEL}`}
                  data-testid={`wedding-package-call-${p.name.split(" ")[0].toLowerCase()}`}
                  className={`mt-8 inline-flex items-center gap-2 rounded-full font-display text-sm px-5 py-2.5 ${p.featured ? "bg-[#1FB6C9] text-white" : "bg-[#0E2A47] text-white"}`}
                >
                  <Phone size={14} /> Call to book
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Availability */}
      <section className="py-24 max-w-6xl mx-auto px-6" id="availability">
        <p className="text-xs uppercase tracking-[0.25em] text-[#1FB6C9] font-body">Live Availability</p>
        <h2 className="mt-4 font-display text-4xl md:text-5xl text-[#0E2A47]">See open dates without leaving the page.</h2>
        <p className="mt-4 text-[#0E2A47]/70 font-body max-w-2xl">Live from Elite STR Hosting Co. — book direct and save 15%.</p>
        <div className="mt-10">
          <Availability />
        </div>
      </section>

      {/* Call for events */}
      <section id="event-inquiry" className="bg-[#0E2A47] text-white py-24">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-xs uppercase tracking-[0.25em] text-[#1FB6C9] font-body">Weddings · Events · Over 24 guests</p>
          <h2 className="mt-4 font-display text-4xl md:text-5xl">Let's talk it through.</h2>
          <p className="mt-4 text-white/75 font-body text-lg leading-relaxed">
            For weddings, milestone celebrations, and events with more than 24 guests, we handle it by phone.
            One quick call and we'll walk you through dates, layout, and everything you need to plan a perfect stay
            at Grandview Estate.
          </p>

          <div className="mt-10 inline-flex flex-col sm:flex-row items-center gap-4 justify-center">
            <a
              href={`tel:${HOST_PHONE_TEL}`}
              data-testid="weddings-call-btn"
              className="inline-flex items-center gap-3 rounded-full bg-[#1FB6C9] px-10 py-5 font-display font-medium text-white text-lg shadow-2xl shadow-[#1FB6C9]/25 hover:-translate-y-0.5 transition-transform"
            >
              <Phone size={22} /> Call {HOST_PHONE_DISPLAY}
            </a>
            <a
              href={`sms:${HOST_PHONE_SMS}?body=Hi%20-%20I%27d%20like%20to%20chat%20about%20hosting%20an%20event%20at%20Grandview%20Estate.`}
              data-testid="weddings-text-btn"
              className="inline-flex items-center gap-3 rounded-full border border-white/40 px-10 py-5 font-display font-medium text-white hover:bg-white hover:text-[#0E2A47] transition-colors"
            >
              <MessageSquare size={20} /> Text us instead
            </a>
          </div>

          <p className="mt-8 text-sm text-white/50 font-body">
            Elite STR Hosting Co. · Weddings, milestone birthdays, corporate retreats & celebrations
          </p>
        </div>
      </section>
    </div>
  );
}
